// exclusionClient — 荒らし復旧 第 2 段階「人を外して戻す」の HTTP クライアント
// (vandalism-recovery-ui-2026-09-27.md §3「詳細設計 (2026-10-01 /consult で合意)」)。
//
// サーバ (いずれも owner-gated + 公式部屋のみ。`GET .../generations` が
// `exclusionAvailable: true` を返したときだけ画面から呼ぶ):
//   - `POST /rooms/:roomId/past-canvases/exclusion`
//       body `{ pastCanvasId?: uuid|null, excludeUids?: string[], excludeSids?: uuid[] }`
//       → 202 `{ jobId }` / 404 `exclusion_not_available` / 409 `exclusion_busy` / 400
//   - `GET  /rooms/:roomId/past-canvases/exclusion/:jobId`
//       → `{status:'running', phase}` | `{status:'done', pastCanvasId, snapshotSeq, stats}`
//         | `{status:'failed', error, detail?}`、dispatcher 再起動で job が消えると 404 `job_not_found`
//
// outcome union で返し、期待される失敗では throw しない (pastCanvasClient.ts と同流儀)。

import type { FetchFn, PastCanvasClientDeps } from './pastCanvasClient.js';

/** サーバ `ExclusionPhase | 'queued'`。 */
export type ExclusionJobPhase =
  | 'queued'
  | 'forcing-snapshot'
  | 'loading-log'
  | 'planning'
  | 'loading-base'
  | 'baking'
  | 'publishing';

/** サーバ `ExclusionBakeStats` のうち画面が使うもの (残りは受け流す)。 */
export interface ExclusionBakeStatsView {
  readonly skippedImagepastes: number;
  readonly replayedEvents?: number;
  readonly droppedExcluded?: number;
  readonly totalMs?: number;
}

export interface StartExclusionInput {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  readonly adminToken?: string;
  /** `null` / 省略 = 現行文書。 */
  readonly pastCanvasId?: string | null;
  readonly excludeUids: readonly string[];
  readonly excludeSids: readonly string[];
  readonly signal?: AbortSignal;
}

export type StartExclusionOutcome =
  | { readonly outcome: 'ok'; readonly jobId: string }
  | { readonly outcome: 'unauthorized' }
  /** 404: 部屋が無い / 第 2 段階が使えない部屋 (`exclusion_not_available`)。 */
  | { readonly outcome: 'not-available' }
  /** 409: 別の復旧処理が走っている。 */
  | { readonly outcome: 'busy' }
  /** 400: body 不正。 */
  | { readonly outcome: 'invalid' }
  | { readonly outcome: 'error' };

export type ExclusionJobState =
  | { readonly status: 'running'; readonly phase: ExclusionJobPhase }
  | {
      readonly status: 'done';
      readonly pastCanvasId: string;
      readonly snapshotSeq: number;
      readonly stats: ExclusionBakeStatsView;
    }
  | { readonly status: 'failed'; readonly error: string; readonly detail?: unknown };

export type FetchExclusionJobOutcome =
  | { readonly outcome: 'ok'; readonly job: ExclusionJobState }
  | { readonly outcome: 'unauthorized' }
  /** 404: job が無い (dispatcher 再起動で消えた / 期限切れ) か、部屋で使えない。 */
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'error' };

const defaultFetchFn: FetchFn = (input, init) => {
  const f = globalThis.fetch as
    | ((url: string, init?: RequestInit) => Promise<Response>)
    | undefined;
  if (f === undefined) throw new Error('globalThis.fetch is not available');
  return f.call(globalThis, input, init as RequestInit | undefined);
};

const headersFor = (adminToken: string | undefined, json: boolean): Record<string, string> => {
  const h: Record<string, string> = {};
  if (adminToken !== undefined && adminToken.length > 0) h.Authorization = `Bearer ${adminToken}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

export const startExclusion = async (
  input: StartExclusionInput,
  deps: PastCanvasClientDeps = {},
): Promise<StartExclusionOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const url = `${baseUrl}/rooms/${encodeURIComponent(input.roomId)}/past-canvases/exclusion`;
  const init: {
    signal?: AbortSignal;
    headers?: Record<string, string>;
    method?: string;
    body?: string;
  } = {
    method: 'POST',
    headers: headersFor(input.adminToken, true),
    body: JSON.stringify({
      pastCanvasId: input.pastCanvasId ?? null,
      excludeUids: input.excludeUids,
      excludeSids: input.excludeSids,
    }),
  };
  if (input.signal !== undefined) init.signal = input.signal;

  let res: Response;
  try {
    res = await fetchFn(url, init);
  } catch {
    return { outcome: 'error' };
  }
  if (res.status === 401 || res.status === 403) return { outcome: 'unauthorized' };
  if (res.status === 404) return { outcome: 'not-available' };
  if (res.status === 409) return { outcome: 'busy' };
  if (res.status === 400) return { outcome: 'invalid' };
  if (res.status !== 202 && res.status !== 200) return { outcome: 'error' };
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { outcome: 'error' };
  }
  const jobId = (raw as { jobId?: unknown } | null)?.jobId;
  if (typeof jobId !== 'string' || jobId.length === 0) return { outcome: 'error' };
  return { outcome: 'ok', jobId };
};

const PHASES = new Set<string>([
  'queued',
  'forcing-snapshot',
  'loading-log',
  'planning',
  'loading-base',
  'baking',
  'publishing',
]);

const parseJob = (raw: unknown): ExclusionJobState | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (o.status === 'running') {
    // 未知の phase (サーバが先に進んだ) は 'queued' 扱いにして落とさない。
    const phase = typeof o.phase === 'string' && PHASES.has(o.phase) ? o.phase : 'queued';
    return { status: 'running', phase: phase as ExclusionJobPhase };
  }
  if (o.status === 'done') {
    if (typeof o.pastCanvasId !== 'string' || typeof o.snapshotSeq !== 'number') return null;
    const stats = (typeof o.stats === 'object' && o.stats !== null ? o.stats : {}) as Record<
      string,
      unknown
    >;
    return {
      status: 'done',
      pastCanvasId: o.pastCanvasId,
      snapshotSeq: o.snapshotSeq,
      stats: {
        skippedImagepastes:
          typeof stats.skippedImagepastes === 'number' ? stats.skippedImagepastes : 0,
        ...(typeof stats.replayedEvents === 'number'
          ? { replayedEvents: stats.replayedEvents }
          : {}),
        ...(typeof stats.droppedExcluded === 'number'
          ? { droppedExcluded: stats.droppedExcluded }
          : {}),
        ...(typeof stats.totalMs === 'number' ? { totalMs: stats.totalMs } : {}),
      },
    };
  }
  if (o.status === 'failed') {
    if (typeof o.error !== 'string') return null;
    return {
      status: 'failed',
      error: o.error,
      ...(o.detail !== undefined ? { detail: o.detail } : {}),
    };
  }
  return null;
};

export const fetchExclusionJob = async (
  input: {
    readonly dispatcherUrl: string;
    readonly roomId: string;
    readonly jobId: string;
    readonly adminToken?: string;
    readonly signal?: AbortSignal;
  },
  deps: PastCanvasClientDeps = {},
): Promise<FetchExclusionJobOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const url = `${baseUrl}/rooms/${encodeURIComponent(input.roomId)}/past-canvases/exclusion/${encodeURIComponent(input.jobId)}`;
  const init: { signal?: AbortSignal; headers?: Record<string, string> } = {
    headers: headersFor(input.adminToken, false),
  };
  if (input.signal !== undefined) init.signal = input.signal;

  let res: Response;
  try {
    res = await fetchFn(url, init);
  } catch {
    return { outcome: 'error' };
  }
  if (res.status === 401 || res.status === 403) return { outcome: 'unauthorized' };
  if (res.status === 404) return { outcome: 'not-found' };
  if (res.status !== 200) return { outcome: 'error' };
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { outcome: 'error' };
  }
  const job = parseJob(raw);
  return job === null ? { outcome: 'error' } : { outcome: 'ok', job };
};
